import React from 'react';

import { WEBHOOKLOGS } from '../../../constants/applicationConstants';
import { listWebHookLogs } from '../Services/WebHookLogsService';
import {
  normalizeWebhookLog,
  WEBHOOK_PAGE_SIZE,
} from '../Components/WebHookTimeline/webHookTimelineUtils';

export const DEFAULT_WEBHOOK_LOG_FILTERS = {
  searchTerm: '',
  fromDate: '',
  toDate: '',
  status: 'all',
};

const DEFAULT_SORT = [
  {
    column: 'created_date',
    order: 'desc',
  },
];

const buildWebhookSearchFilters = (searchTerm) => {
  if (!searchTerm) {
    return [];
  }

  return [
    {
      ColumnName: 'webhook_log_id',
      Op: 'eq',
      ColumnValue: searchTerm,
    },
    {
      ColumnName: 'author',
      Op: 'contains',
      ColumnValue: searchTerm,
    },
    {
      ColumnName: 'repo',
      Op: 'contains',
      ColumnValue: searchTerm,
    },
    {
      ColumnName: 'status',
      Op: 'eq',
      ColumnValue: searchTerm,
    },
    {
      ColumnName: 'commit_date',
      Op: 'eq',
      ColumnValue: searchTerm,
    },
    {
      ColumnName: 'created_date',
      Op: 'eq',
      ColumnValue: searchTerm,
    },
  ];
};

const buildWebhookDateFilters = ({ fromDate, toDate }) => {
  const requestedFilters = [];

  if (fromDate) {
    requestedFilters.push({
      ColumnName: 'created_date',
      Op: 'gte',
      ColumnValue: fromDate,
    });
  }

  if (toDate) {
    requestedFilters.push({
      ColumnName: 'created_date',
      Op: 'lte',
      ColumnValue: toDate,
    });
  }

  return requestedFilters;
};

const buildServerFilters = (filters = DEFAULT_WEBHOOK_LOG_FILTERS) => {
  const normalizedFilters = {
    ...DEFAULT_WEBHOOK_LOG_FILTERS,
    ...(filters || {}),
  };
  const searchTerm = String(normalizedFilters.searchTerm ?? '').trim();
  const fromDate = String(normalizedFilters.fromDate ?? '').trim();
  const toDate = String(normalizedFilters.toDate ?? '').trim();
  const status = String(normalizedFilters.status ?? '')
    .trim()
    .toLowerCase();
  const statusFilterValue = status === 'failed' ? 'fail' : status;

  return [
    ...buildWebhookSearchFilters(searchTerm),
    ...buildWebhookDateFilters({
      fromDate,
      toDate,
    }),
    ...(status && status !== 'all'
      ? [
          {
            ColumnName: 'status',
            Op: 'eq',
            ColumnValue: statusFilterValue,
          },
        ]
      : []),
  ];
};

const getFilterOperator = (filters = DEFAULT_WEBHOOK_LOG_FILTERS) =>
  String(filters?.searchTerm ?? '').trim() ? 'OR' : 'AND';

const useWebHookTimeline = ({
  filters = DEFAULT_WEBHOOK_LOG_FILTERS,
  pageNumber = 1,
  pageSize = WEBHOOK_PAGE_SIZE,
} = {}) => {
  const [logs, setLogs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [totalRecordCount, setTotalRecordCount] = React.useState(0);
  const [stats, setStats] = React.useState(null);
  const [pagination, setPagination] = React.useState(null);

  const loadPage = React.useCallback(
    async ({ requestedPage = pageNumber, refresh = false } = {}) => {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setErrorMessage('');

      try {
        const response = await listWebHookLogs({
          pageSize,
          pageNumber: requestedPage,
          filters: buildServerFilters(filters),
          filterOperator: getFilterOperator(filters),
          sort: DEFAULT_SORT,
          moduleName: WEBHOOKLOGS,
        });

        if (response?.isSuccess === false) {
          throw new Error(
            response?.message ||
              response?.errorMessage ||
              'Failed to load webhook logs.'
          );
        }

        const nextItems = Array.isArray(response?.webhooklogs)
          ? response.webhooklogs.map(normalizeWebhookLog)
          : [];

        setLogs(nextItems);
        setStats(response?.stats ?? null);
        setPagination(response?.pagination ?? null);
        setTotalRecordCount(Number(response?.totalRecordCount ?? 0));
      } catch (error) {
        setLogs([]);
        setStats(null);
        setPagination(null);
        setTotalRecordCount(0);
        setErrorMessage(error?.message || 'Unable to load webhook logs.');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [filters, pageNumber, pageSize]
  );

  React.useEffect(() => {
    loadPage({
      requestedPage: pageNumber,
    });
  }, [loadPage, pageNumber]);

  const refreshLogs = React.useCallback(async () => {
    await loadPage({
      requestedPage: pageNumber,
      refresh: true,
    });
  }, [loadPage, pageNumber]);

  return {
    logs,
    loading,
    refreshing,
    errorMessage,
    totalRecordCount,
    stats,
    pagination,
    refreshLogs,
  };
};

export default useWebHookTimeline;
